import debounce from 'debounce';
import glyphy, { type GlyphyStyles } from 'glyphy';
import opentype from 'opentype.js';

import { AssetFileType } from 'types/AssetFileType';
import { DynamicPropertyEvent } from 'types/DynamicPropertyEvent';
import { DynamicPropertyStatus } from 'types/DynamicPropertyStatus';
import { DynamicPropertyType } from 'types/DynamicPropertyType';

import { ifAsset } from '../casts';
import { type DynamicPropertyData } from '../data';
import { flatMapProperties } from '../transform';
import { getOwningCreative, getRoot } from '../tree';
import DynamicGroup from './Group';
import DEFAULT_SVG from './SVGTextDefaultValue';

const svgPathDecimalPlaces = 1;

const TEXT_ALIGN_VALUES = ['left', 'center', 'right'] as const;
const VERTICAL_ALIGN_VALUES = ['top', 'middle', 'bottom'] as const;
const STROKE_LINECAP_VALUES = ['butt', 'round', 'square'] as const;

// This is likely duplication of what already exists, but I consider it a
// shim for now, to fetch exactly what Glyphy needs without getting
// entangled in the semantics of all the other DynamicProperty stuff.

// The value the retired getObject() produced for a child: numeric types as
// their getString() - unit-bearing for UnitNumbers ("24px"), and '0' for
// an empty value, which matters to glyphy (an empty strokeOpacity must
// stay 0, not fall back to its default of 1) - and everything else as its
// raw value.  Keyed by the child's *stored* type, which comes from
// template data and needn't match setDefault's.
function getRaw(group: DynamicGroup, name: string): unknown {
  const dp = group.getChild(name);
  if (!dp) return undefined;
  switch (dp.type) {
    case DynamicPropertyType.NUMBER:
    case DynamicPropertyType.PIXELS:
    case DynamicPropertyType.PERCENTAGE:
      return dp.getString();
  }
  return dp.value;
}

// For glyphy's `string | number` fields. Strings pass through unparsed:
// glyphy reads unitless numbers as px and parses CSS dimensions itself, and a
// child stored as Text ("1.2", "50%") is as valid there as a Pixels one.
function getStringOrNumber(
  group: DynamicGroup,
  name: string
): string | number | undefined {
  const raw = getRaw(group, name);
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  return undefined;
}

function getColor(group: DynamicGroup, name: string): string | undefined {
  const raw = getRaw(group, name);
  return typeof raw === 'string' ? raw : undefined;
}

function getBoolean(group: DynamicGroup, name: string): boolean | undefined {
  const dp = group.getChild(name);
  if (dp && typeof dp.value === 'boolean') {
    return dp.value;
  }
  return undefined;
}

function getEnum<T extends ReadonlyArray<string>>(
  group: DynamicGroup,
  name: string,
  choices: T
): T[number] | undefined {
  const dp = group.getChild(name);
  if (dp && typeof dp.value === 'string' && choices.includes(dp.value)) {
    return dp.value as T[number];
  }
  return undefined;
}

export default class SVGText extends DynamicGroup {
  // TODO: this is incomplete
  //  note: that ^^^ is an old Craig comment that dates back to 2022; I
  //  don't know in which way it's incomplete or what was intended
  type = DynamicPropertyType.SVGText;
  // value = [];
  setDefault = () => {
    // Falls back to no font when there is no owning creative, exactly as it
    // already did for a creative with no font assets.
    const font =
      getOwningCreative(this)?.assets.all.find(
        (i) => i.getFileType() === AssetFileType.FONT
      )?.id || '';
    this.add('font', DynamicPropertyType.ASSET, font);
    this.add('text', DynamicPropertyType.TEXT, 'Lorem ipsum\n dolor amet');
    this.add('color', DynamicPropertyType.COLOR, '#CCCCCC');
    this.add('left', DynamicPropertyType.PIXELS, 10);
    this.add('top', DynamicPropertyType.PIXELS, 10);
    this.add('offsetX', DynamicPropertyType.PIXELS, 0);
    this.add('offsetY', DynamicPropertyType.PIXELS, 0);
    this.add('width', DynamicPropertyType.PIXELS, 300);
    this.add('height', DynamicPropertyType.PIXELS, 95);
    this.add('fontSize', DynamicPropertyType.PIXELS, 24);
    this.add('lineHeight', DynamicPropertyType.PIXELS, 30);
    this.add('textAlign', DynamicPropertyType.TEXT, 'center');
    this.add('verticalAlign', DynamicPropertyType.TEXT, 'middle');
    this.add('letterSpacing', DynamicPropertyType.PIXELS, 1);
    this.add('dropshadowColor', DynamicPropertyType.COLOR, '#000000');
    this.add('dropshadowBlurX', DynamicPropertyType.NUMBER, 0);
    this.add('dropshadowBlurY', DynamicPropertyType.NUMBER, 0);
    this.add('dropshadowOffsetX', DynamicPropertyType.PIXELS, 0);
    this.add('dropshadowOffsetY', DynamicPropertyType.PIXELS, 0);
    this.add('dropshadowOpacity', DynamicPropertyType.NUMBER, 0);
    this.add('strokeColor', DynamicPropertyType.COLOR, '#000000');
    this.add('strokeLinecap', DynamicPropertyType.TEXT, 'round');
    this.add('strokeWidth', DynamicPropertyType.PIXELS, 0);
    this.add('strokeOpacity', DynamicPropertyType.NUMBER, 0);
    this.add('maskRows', DynamicPropertyType.BOOLEAN, false);
    this.add('boundingBox', DynamicPropertyType.BOOLEAN, false);
    this.add('SVGData', DynamicPropertyType.SVGData, DEFAULT_SVG);
  };

  constructor(
    parent: DynamicGroup,
    name: string,
    value: DynamicPropertyData[]
  ) {
    super(parent, name, value);
    // Cancel any in-flight debounce when this store is reset/disposed, so a
    // pending render can't fire into a torn-down object. `clear` closes over
    // its own timer, so it is safe to pass unbound.
    //
    // FIXME never cleared, for the same reason as DynamicGroup's own
    // registration (see its constructor): nothing in this hierarchy calls
    // disposer.clear(), so the cancel described above never happens.
    this.disposer.add(this.render.clear);
    // this.becomeObservable();
  }

  /**
   * Re-renders rather than passing the change straight up. render() dispatches
   * CHANGED once the new SVGData is set, so listeners - the ad preview's reload
   * among them - see the re-rendered markup, not the stale one. No CHANGED
   * follows when there is nothing to render (no font, or the owner isn't an
   * active ad) or the render fails.
   */
  protected onChildChanged(): void {
    this.render();
  }

  getString = () => {
    return this.getChild('text')?.getString() || '';
  };

  getPreviewData = () => {
    const font = this.getChild('font');
    if (!font || !font.value) return {};

    return flatMapProperties(this.properties, function* (p) {
      if (p.type === 'SVGData') {
        yield ['__type', 'svgData'];
        yield ['__value', p.value];
      } else {
        yield [p.name, p.getPreviewData()];
      }
    });
  };

  getCSVData = () => {
    return JSON.stringify(
      flatMapProperties(this.properties, function* (p) {
        if (p.type !== 'SVGData') {
          yield [p.name, p.getCSVData()];
        }
      })
    );
  };

  renderTextPaths = async (forceRender = false) => {
    const root = getRoot(this);
    const ad = (root as any).parentDocument;
    if (!ad || (!ad.active && !forceRender)) return;
    const labelValue = root.getChild('label')?.value;
    const labelString: string | undefined =
      labelValue == null ? undefined : String(labelValue);
    await this.forceRenderTextPaths(labelString);
  };

  render = debounce(this.renderTextPaths, 10);

  private async forceRenderTextPaths(diagLabel: string | undefined) {
    const logPrefix = `[SVGText.renderTextPaths]`;
    const logLabel =
      diagLabel == null ? '(no label)' : JSON.stringify(diagLabel);
    const logName = JSON.stringify(this.name);
    let logCoord = `ad ${logLabel}, property ${logName}`;
    let didChange = false;

    this.status = DynamicPropertyStatus.LOADING;
    try {
      const fontAsset = ifAsset(this.getChild('font'));
      if (!fontAsset) {
        console.warn(`${logPrefix} No font asset for ${logCoord}`);
        return;
      }

      const text = this.getChild('text')?.getString() || '';
      const styles = this.scrapeStyleParameters();

      const fontAssetURL = fontAsset.assetURL;
      if (!fontAssetURL) {
        throw new Error("Font asset doesn't exist");
      }

      logCoord += `, font URL ${JSON.stringify(fontAssetURL)}`;
      const fontInstance = await opentype.load(fontAssetURL);

      const svgData = glyphy.svgMarkup(text, fontInstance, styles, {
        decimalPlaces: svgPathDecimalPlaces,
      });

      // we COULD compare the rendered SVG text, but correctness first, then optimization
      didChange = true;
      const svgDataProperty = this.getChild('SVGData');
      if (svgDataProperty) {
        svgDataProperty.set(svgData, true);
      } else {
        this.add('SVGData', DynamicPropertyType.SVGData, svgData);
      }
    } catch (renderError) {
      console.error(`${logPrefix} failed for ${logCoord}`, renderError);
      // But don't touch SVGData, because it may be valid from a previous render.
    } finally {
      // There's no .ERROR status and no code that handles it,
      // so reverting to .READY is our only choice
      this.status = DynamicPropertyStatus.READY;
      if (didChange) {
        this.dispatch(DynamicPropertyEvent.CHANGED);
      }
    }
  }

  private scrapeStyleParameters(): GlyphyStyles {
    return {
      color: getColor(this, 'color'),
      fontSize: getStringOrNumber(this, 'fontSize'),
      textAlign: getEnum(this, 'textAlign', TEXT_ALIGN_VALUES),
      verticalAlign: getEnum(this, 'verticalAlign', VERTICAL_ALIGN_VALUES),
      letterSpacing: getStringOrNumber(this, 'letterSpacing'),
      lineHeight: getStringOrNumber(this, 'lineHeight'),
      top: getStringOrNumber(this, 'top'),
      left: getStringOrNumber(this, 'left'),
      offsetX: getStringOrNumber(this, 'offsetX'),
      offsetY: getStringOrNumber(this, 'offsetY'),
      width: getStringOrNumber(this, 'width'),
      height: getStringOrNumber(this, 'height'),
      boundingBox: getBoolean(this, 'boundingBox'),
      dropshadowColor: getColor(this, 'dropshadowColor'),
      // glyphy renders `filter: drop-shadow`, which takes a single, circular
      // blur radius. dropshadowBlur is that value, though setDefault never
      // creates it - it's read in case stored data has one. dropshadowBlurX/Y
      // are the legacy elliptical pair from glyphy's earlier feDropShadow
      // rendering; setDefault still creates them, and glyphy resolves all
      // three to their max (see maxValidPx in glyphy/src/defaults.ts).
      dropshadowBlur: getStringOrNumber(this, 'dropshadowBlur'),
      dropshadowBlurX: getStringOrNumber(this, 'dropshadowBlurX'),
      dropshadowBlurY: getStringOrNumber(this, 'dropshadowBlurY'),
      dropshadowOffsetX: getStringOrNumber(this, 'dropshadowOffsetX'),
      dropshadowOffsetY: getStringOrNumber(this, 'dropshadowOffsetY'),
      dropshadowOpacity: getStringOrNumber(this, 'dropshadowOpacity'),
      strokeColor: getColor(this, 'strokeColor'),
      strokeLinecap: getEnum(this, 'strokeLinecap', STROKE_LINECAP_VALUES),
      strokeWidth: getStringOrNumber(this, 'strokeWidth'),
      strokeOpacity: getStringOrNumber(this, 'strokeOpacity'),
      maskRows: getBoolean(this, 'maskRows'),
    };
  }
}
